import { Notifier } from '@airbrake/browser';
import { detect } from 'detect-browser';

let airbrakeInstance = null;

const SEVERITY_LEVEL = {
  DEBUG: 'debug',
  ERROR: 'error',
  INFO: 'info'
};

const makeLoggerWithSeverity = severity => {
  return (err, annotations = {}) => {
    if (!airbrakeInstance) {
      throw new Error('Airbrake has not been initialized');
    }

    airbrakeInstance.notify({
      error: err,
      context: { severity },
      ...annotations
    });
  };
};

const logger = {
  debug: makeLoggerWithSeverity(SEVERITY_LEVEL.DEBUG),
  error: makeLoggerWithSeverity(SEVERITY_LEVEL.ERROR),
  info: makeLoggerWithSeverity(SEVERITY_LEVEL.INFO)
};

const initialize = ({ appVersion, environment, isDebug = false, isSdk, projectId, projectKey }) => {
  if (!(appVersion && projectId && projectKey)) {
    throw new Error('Airbrake requires appVersion, projectId, and projectKey');
  }

  if (isDebug) {
    /* eslint-disable-next-line no-console */
    console.warn('%cAirbrake logger is in debug mode', 'color:yellow');
  }

  airbrakeInstance = new Notifier({
    environment,
    projectId,
    projectKey
  });

  const browser = detect();
  const version = parseInt(browser?.version || '0', 10);
  const name = browser?.name;
  const unsupportedBrowsers = ['safari', 'ie', 'ios'];

  if (unsupportedBrowsers.includes(name) && version < 11) {
    airbrakeInstance.addFilter(() => null); // nullify notices for old browsers
  }

  // add environment and appVersion to context
  airbrakeInstance.addFilter(notice => {
    notice.context.environment = environment;
    notice.context.isSdk = isSdk;
    notice.context.version = appVersion;

    return notice;
  });

  // prevent pushing notices to Airbrake API when debugging
  airbrakeInstance.addFilter(notice => {
    if (!isDebug) {
      return notice;
    }

    /* eslint-disable no-console */
    console.group('%cAirbrake logger is in debug mode, logging notice to console:', 'color:yellow');
    console.log(notice);
    console.groupEnd();
    /* eslint-enable no-console */

    return null;
  });

  return logger;
};

const getInstance = () => {
  if (!airbrakeInstance) {
    throw new Error('Airbrake has not been initialized');
  }

  return logger;
};

export { initialize, getInstance };
